import type React from "react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { MessageStatusChangeRequestV2 } from "@/api/types";
import ImageParty from "@/assets/images/party.png";
import { Form } from "@/components/Form/Form";
import { FormTagSelect } from "@/components/Form/FormTagSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useTranslation } from "@/translations";

const FORM_ID = "community-message-close-post-solved-modal-form";

type CommunityMessageClosePostSolvedReasonModalProps = ModalBaseProps & {
  messageId: string;
};

type CommunityMessageClosePostSolvedModalFormValues = {
  reason: Extract<MessageStatusChangeRequestV2["reason"], "solvedHelpInsideCommunity" | "solvedOther">[];
  details?: string;
};

export function CommunityMessageClosePostSolvedReasonModal({
  messageId,
  isOpened,
  onOpenChange,
}: CommunityMessageClosePostSolvedReasonModalProps): React.ReactNode {
  const [isCompleted, setIsCompleted] = useState(false);

  const { t } = useTranslation();
  const form = useForm<CommunityMessageClosePostSolvedModalFormValues>({
    defaultValues: {
      reason: [],
      details: undefined,
    },
  });

  useEffect(() => {
    form.reset();
  }, [isOpened, form]);

  const selectedReason = useWatch({
    control: form.control,
    name: "reason",
  })[0];
  const updateMessageStatus = communityFeedMutations.useUpdateMessageStatus();

  const handleSubmit = async (data: CommunityMessageClosePostSolvedModalFormValues) => {
    if (data.reason.length === 0) return;

    await updateMessageStatus.mutateAsync({
      messageId,
      data: {
        newStatus: "archived",
        reason: data.reason[0],
        details: data.details,
      },
    });

    setIsCompleted(true);
  };

  return (
    <Modal.Root
      title={
        isCompleted
          ? t("component.community-post.close-post-confirm-modal.title.success")
          : t("component.community-post.close-post-confirm-modal.title")
      }
      {...{ isOpened, onOpenChange }}
    >
      {!isCompleted && (
        <>
          <Modal.Body>
            <Form.Root formMethods={form} id={FORM_ID} onSubmit={handleSubmit}>
              <Form.Body>
                <FormField label={t("component.community-post.close-post-solved-modal.reason-field.label")} required>
                  <FormTagSelect<CommunityMessageClosePostSolvedModalFormValues, "reason">
                    data-testid="closing-post-solved-modal-options-container"
                    name="reason"
                    items={[
                      {
                        label: t(
                          "component.community-post.close-post-solved-modal.reason-field.option.inside-community",
                        ),
                        value: "solvedHelpInsideCommunity",
                      },
                      {
                        label: t("component.community-post.close-post-solved-modal.reason-field.option.other"),
                        value: "solvedOther",
                      },
                    ]}
                    rules={{
                      validate: {
                        required: (value) => {
                          if (value.length === 0) {
                            return t("component.community-post.close-post-solved-modal.details-field.error");
                          }
                        },
                      },
                    }}
                  />
                </FormField>
                {selectedReason && (
                  <FormField label={t("component.community-post.close-post-solved-modal.details-field.label")}>
                    <FormTextArea<CommunityMessageClosePostSolvedModalFormValues, "details">
                      data-testid="closing-post-solved-modal-details-textarea"
                      name="details"
                      placeholder={
                        selectedReason === "solvedHelpInsideCommunity"
                          ? t(
                              "component.community-post.close-post-solved-modal.details-field.placeholder.inside-community",
                            )
                          : t("component.community-post.close-post-solved-modal.details-field.placeholder.other")
                      }
                    />
                  </FormField>
                )}
              </Form.Body>
            </Form.Root>
          </Modal.Body>
          <Modal.Actions>
            <Button
              data-testid="confirm-issue-solved-btn"
              type="submit"
              form={FORM_ID}
              isLoading={updateMessageStatus.isPending}
            >
              {t("component.community-post.close-post-solved-modal.btn")}
            </Button>
          </Modal.Actions>
        </>
      )}
      {isCompleted && (
        <>
          <Modal.Body>
            <section className="mx-auto flex flex-col items-center gap-space-md py-space-md">
              <img height={96} width={96} src={ImageParty} alt="Success" />
              <p className="text-center text-text-secondary">
                {t("component.community-post.close-post-confirm-modal.body.success")}
              </p>
            </section>
          </Modal.Body>
          <Modal.Actions>
            <Modal.Close>
              <Button>{t("common.action.continue")}</Button>
            </Modal.Close>
          </Modal.Actions>
        </>
      )}
    </Modal.Root>
  );
}
